import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import { Button, Upload, Alert, Form, Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import { RcFile } from 'antd/lib/upload'
import { useSelector, useDispatch } from 'react-redux'
import { DatePicker } from '../../../../../components/Form'
import { startOfMonth, endOfMonth } from '../../../../../config/dayjsConfig'
import { authTokenSelector } from '../../../../../redux/session/authentication/selectors'
import { filtersSelector } from '../../../../../redux/context/filters/selectors'
import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import { notificationAction } from '../../../../../redux/middleware/actions'
import { AppDispatch } from '../../../../../redux/store'
import CtxScenarioLockSelect from '../../../../../features/scenario/components/select/CtxScenarioLockSelect'

const BudgetingCSVImport = () => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const [files, setFiles] = useState<RcFile[]>([])
  const [fileLoading, setFileLoading] = useState<boolean>(false)
  const { budgetingScenario } = useSelector(filtersSelector)
  const token = useSelector(authTokenSelector)
  const companyId = useSelector(contextCompanyIdSelector)

  const resetForm = () => {
    form.resetFields()
  }

  useEffect(resetForm, [companyId])

  const handleSubmit = () => {
    form.validateFields().then(formvalues => {
      setFileLoading(true)
      const formData = new FormData()
      files.forEach((file: RcFile) => {
        formData.append('file', file)
      })
      formData.append('startDate', startOfMonth(formvalues.startDate))
      formData.append('endDate', endOfMonth(formvalues.endDate))

      axios({
        method: 'post',
        headers: {
          'X-Token': token ?? '',
          'Content-Type': 'multipart/form-data'
        },
        url: `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/budgeting/budget-deltas/import`,
        params: {
          budgetingScenarioId: formvalues.budgetingScenario
        },
        data: formData
      })
        .then(() => {
          setFileLoading(false)
          dispatch(notificationAction({ type: 'success', message: 'IMPORT_SUCCESS' }))
        })
        .catch(error => {
          setFileLoading(false)
          dispatch(
            notificationAction({
              type: 'error',
              message: 'IMPORT_ERROR',
              description: error?.response?.data?.message
            })
          )
        })
    })
  }

  const getCsvTemplate = () => {
    axios({
      url: `${process.env.REACT_APP_BACKEND_URL}/api/templates/budget-import`,
      method: 'GET',
      headers: {
        'X-Token': token ?? ''
      },
      responseType: 'blob' // important
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'finadeck-budget-import-template.csv')
      document.body.appendChild(link)
      link.click()
    })
  }

  const options = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    headers: {
      'X-Token': token || ''
    },
    fileList: files,
    onRemove: () => {
      setFiles([])
    },
    beforeUpload: (file: RcFile) => {
      setFiles([file])
      return false
    }
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <Button icon={<DownloadOutlined />} onClick={getCsvTemplate}>
        {t('budgetingImportPage:get-template')}
      </Button>
      <Form layout="vertical" form={form} colon onFinish={handleSubmit}>
        <Form.Item
          name="budgetingScenario"
          style={{ maxWidth: 400 }}
          label={t('global:scenario')}
          initialValue={budgetingScenario?.isLocked ? undefined : budgetingScenario?.id}
        >
          <CtxScenarioLockSelect />
        </Form.Item>
        <Form.Item
          name="startDate"
          rules={[{ required: true, message: t('budgetingImportPage:select-month') }]}
          style={{ maxWidth: 400 }}
          label={t('budgetingImportPage:start-date')}
        >
          <DatePicker
            picker="month"
            placeholder={t('budgetingImportPage:select-month')}
            onChange={date => {
              if (date) {
                form.setFieldValue('startDate', startOfMonth(date))
              }
            }}
          />
        </Form.Item>
        <Form.Item
          name="endDate"
          rules={[{ required: true, message: t('budgetingImportPage:select-month') }]}
          style={{ maxWidth: 400 }}
          label={t('budgetingImportPage:end-date')}
        >
          <DatePicker
            picker="month"
            placeholder={t('budgetingImportPage:select-month')}
            onChange={date => {
              if (date) {
                form.setFieldValue('endDate', endOfMonth(date))
              }
            }}
          />
        </Form.Item>
        <Form.Item
          name="file"
          rules={[{ required: true }]}
          style={{ maxWidth: 600 }}
          label={t('budgetingImportPage:import-file')}
        >
          <Upload.Dragger {...options}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('budgetingImportPage:upload-text')}</p>
            <Alert
              style={{ textAlign: 'left', margin: 16 }}
              message={t('global:attention')}
              description={t('budgetingImportPage:upload-description')}
              type="info"
              showIcon
            />
          </Upload.Dragger>
        </Form.Item>
        <Form.Item>
          <Button loading={fileLoading} type="primary" htmlType="submit" disabled={files.length === 0}>
            {t('global:save')}
          </Button>
        </Form.Item>
      </Form>
      <Divider />
      <Alert type="info" description={t('budgetingImportPage:info-text')} />
    </div>
  )
}

export default BudgetingCSVImport
